export const CAMPING_YEAR = 2026;
export const CAMPING_COUNT = 8;

// Canonical origin, no trailing slash. Must match the <link rel="canonical">
// in index.html — the prerender step reads the origin from there, and JSON-LD
// built in components needs the same value to emit absolute URLs.
export const SITE_ORIGIN = "https://www.campingparquetayrona.com";

// @id of the site-wide Organization node declared in index.html's JSON-LD.
// Reference this instead of re-declaring the Organization in another block.
export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

// Bump this whenever prices, closure dates, or campsite details change.
// Shown on-page as a freshness signal and should stay roughly in sync with
// sitemap.xml's <lastmod> (that one is auto-set to the build date).
export const LAST_UPDATED_ISO = "2026-10-08";
export const LAST_UPDATED_DISPLAY = "8 de octubre de 2026";

// ---------------------------------------------------------------------------
// Closure dates — single source of truth.
// The park closes 3 times a year for ecological recovery and indigenous
// cultural calendar reasons. This data used to be hardcoded independently in
// ParkClosures.tsx, lib/closures.ts, FAQ.tsx, and index.html's
// FAQPage JSON-LD — keep it here and import it everywhere instead (index.html
// is plain HTML and can't import this, so keep it manually in sync there).
// ---------------------------------------------------------------------------
export interface ClosureWindow {
  /** 1-12 */
  startMonth: number;
  startDay: number;
  /** 1-12 */
  endMonth: number;
  endDay: number;
  /** Compact form for calendar bars / banners, e.g. "1–15 feb" */
  short: string;
  /** Full prose form, e.g. "1 al 15 de febrero" */
  full: string;
  reason: string;
}

export const CLOSURE_WINDOWS: ClosureWindow[] = [
  {
    startMonth: 2,
    startDay: 1,
    endMonth: 2,
    endDay: 15,
    short: "1–15 feb",
    full: "1 al 15 de febrero",
    reason:
      "Saneamiento espiritual en las Dunas. Calendario ecológico y cultural indígena (Kogui, Arhuacos, Wiwa y Kankuamos) + monitoreo del IDEAM.",
  },
  {
    startMonth: 6,
    startDay: 1,
    endMonth: 6,
    endDay: 15,
    short: "1–15 jun",
    full: "1 al 15 de junio",
    reason:
      'Estrategia #RespiraTayrona: conservación ambiental y fortalecimiento cultural. El parque "respira" para recuperar ecosistemas afectados por el turismo masivo.',
  },
  {
    startMonth: 10,
    startDay: 19,
    endMonth: 11,
    endDay: 2,
    short: "19 oct–2 nov",
    full: "19 de octubre al 2 de noviembre",
    reason:
      "Período NABBATASHI: los animales realizan rituales en honor a la tierra según el calendario sagrado indígena.",
  },
];

// ---------------------------------------------------------------------------
// Currency — COP is the source of truth for every price on the site; USD is
// always derived from it through formatUSD() below, never hardcoded, so a
// price or exchange-rate update never needs a second manual edit.
// Rate is an editorial approximation for orientation, not a live feed —
// update this one number when it drifts noticeably from the real market rate.
// ---------------------------------------------------------------------------
export const COP_PER_USD = 4000;

const withThousands = (n: number): string => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/** e.g. formatCOP(36500) -> "$36.500" */
export const formatCOP = (cop: number): string => `$${withThousands(cop)}`;

/** e.g. formatUSD(36500) -> "US$9" (sub-$10 amounts always show 2 decimals: US$4.50) */
export const formatUSD = (cop: number): string => {
  const usd = cop / COP_PER_USD;
  if (usd < 10) return `US$${usd.toFixed(2)}`;
  return `US$${Math.round(usd).toLocaleString("en-US")}`;
};

// ---------------------------------------------------------------------------
// Entry fees — single source of truth.
// Verified against the primary source: Resolución No. 551 de 2025 (19 dic. 2025),
// Parques Nacionales Naturales de Colombia, Artículo 6 — "Áreas protegidas con
// temporadas", fila Parque Nacional Natural Tayrona. Effective 1 enero 2026.
// This is a live, government-set tariff and can change — always show the
// disclaimer below next to any price pulled from here.
// ---------------------------------------------------------------------------
export interface EntryFeeTier {
  group: string;
  /** COP, temporada baja */
  lowSeasonCOP: number;
  /** COP, temporada alta */
  highSeasonCOP: number;
  /** COP, sector Bahía Concha (own fee, same all year) */
  bahiaConchaCOP: number;
}

// Age bands follow the official table: "5 años a menor o igual de 25 años" and
// "Mayores de 25 años" (age 25 is in the young band). Checked against the PNN
// Tayrona page on 2026-10-08.
export const ENTRY_FEES: EntryFeeTier[] = [
  { group: "Colombianos/residentes/CAN, 5 a 25 años", lowSeasonCOP: 27500, highSeasonCOP: 30500, bahiaConchaCOP: 15000 },
  { group: "Colombianos/residentes/CAN, mayores de 25 años", lowSeasonCOP: 36500, highSeasonCOP: 43000, bahiaConchaCOP: 26000 },
  { group: "Extranjeros no residentes", lowSeasonCOP: 81000, highSeasonCOP: 96500, bahiaConchaCOP: 43500 },
  { group: "Nacidos en Santa Marta, 5 a 25 años", lowSeasonCOP: 17500, highSeasonCOP: 30500, bahiaConchaCOP: 11500 },
  { group: "Nacidos en Santa Marta, mayores de 25 años", lowSeasonCOP: 18000, highSeasonCOP: 43000, bahiaConchaCOP: 11500 },
];

export const ENTRY_FEE_DISCLAIMER =
  "Tarifas sujetas a cambio, verifica la tarifa vigente en el sitio oficial de Parques Nacionales antes de viajar. Precios en COP, por persona; varían según nacionalidad/residencia, edad y temporada. El valor en dólares es una conversión aproximada de referencia, no la tarifa oficial.";
